import { PIRSCHZEICHEN } from "@dj/domain/schuss";
import { useEffect, useState } from "react";
import { Button } from "react-aria-components";
import { useNavigate, useParams } from "react-router";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Meldung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { meldungsIdAnzeige } from "../design/Kennung";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { umschlag } from "../plattform/api";
import { beobachten, eintraege, einreihen, type Eintrag } from "../plattform/warteschlange";
import { positionLesen, positionMessen } from "./geraeteposition";
import { useEigeneMeldungen, type Rahmen } from "./lage";

/**
 * Ansicht „Ergänzen“ zu einem eigenen Schuss (oberflaeche.md §8 Nr. 2, V-14; design/README §14 „Ergänzen“): erreicht über
 * `button` „Ergänzen“ im `article` „Schuss <Meldungs-Id>“ der Ansicht „Meldungen“. Die Liste zeigt „Fluchtrichtung“ (oben,
 * weit über dem 3 × 3 der Folgeansicht), „Anschuss“, „Schussausgang“ und „Pirschzeichen“; unten eine Schutzfläche mit dem
 * Übertragungsstand, wo die Folgeansichten ihre meldenden Kacheln haben (K70). Jede Folgeansicht ist ein Ablauf (§5) mit
 * „Zurück“ zur Liste; die Wahl geht ohne Rückgängig-Fenster in die Warteschlange („Ergänzung zum Schuss …“ bzw.
 * „Korrektur zum Schuss …“, §1.5), danach zurück zu „Meldungen“.
 */
type Schritt = "liste" | "Fluchtrichtung" | "Anschuss" | "Schussausgang" | "Pirschzeichen";

const LISTE: Array<{ schritt: Exclude<Schritt, "liste">; symbol: string }> = [
  { schritt: "Fluchtrichtung", symbol: "fluchtrichtung" },
  { schritt: "Anschuss", symbol: "anschuss" },
  { schritt: "Schussausgang", symbol: "korrigieren" },
  { schritt: "Pirschzeichen", symbol: "pirschzeichen" },
];

/** 3 × 3 der Fluchtrichtung, Nord oben (design/README §9.7); `null` = Mitte */
const RASTER: Array<string | null> = ["NW", "N", "NO", "W", null, "O", "SW", "S", "SO"];
const SYMBOL_PIRSCHZEICHEN: Record<string, string> = { "Schweiß": "schweiss", Schnitthaar: "schnitthaar" };

/** Eintrag der Warteschlange zur Meldung des Schusses (Übertragungsstand, Angaben ohne Netz). */
function useMeldungDesSchusses(schuss: string): Eintrag | null {
  const [eintrag, setEintrag] = useState<Eintrag | null>(null);
  useEffect(() => {
    const lesen = () => void eintraege().then((es) => setEintrag(es.find((e) => e.befehl === "SchussMelden" && e.meldungsId === schuss) ?? null));
    lesen();
    return beobachten(lesen);
  }, [schuss]);
  return eintrag;
}

/** Drückjagd eines eigenen Schusses: aus der Warteschlange bzw. aus „eigene Meldungen“ (technisches Feld `_drueckjagd`). */
export function useDrueckjagdDesSchusses(schuss: string): string | null {
  const eintrag = useMeldungDesSchusses(schuss);
  const eigene = useEigeneMeldungen().find((m) => String(m["Schuss"] ?? m._id) === schuss);
  return (eintrag?.daten["Drückjagd"] as string | undefined) ?? (eigene?.["_drueckjagd"] as string | undefined) ?? null;
}

function zeit(d: unknown): string | null {
  const t = d instanceof Date ? d : typeof d === "string" ? new Date(d) : null;
  return t && !Number.isNaN(t.getTime()) ? t.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : null;
}

export function SchussErgaenzen({ rahmen }: { rahmen: Rahmen }) {
  const { id = "", schuss = "" } = useParams();
  const navigate = useNavigate();
  const [schritt, setSchritt] = useState<Schritt>("liste");
  const [fehler, setFehler] = useState<string | null>(null);
  const [karte, setKarte] = useState(false);
  const [eingabe, setEingabe] = useState("");
  const gesperrt = useSperre("ablauf", schritt);
  const meldung = useMeldungDesSchusses(schuss);
  const eigene = useEigeneMeldungen().find((m) => String(m["Schuss"] ?? m._id) === schuss);

  const wildart = (eigene?.["Ansprache"] as string | undefined) ?? (eigene?.["Wildart"] as string | undefined)
    ?? [meldung?.daten["Wildart"], meldung?.daten["Altersklasse"]].filter(Boolean).join(" ");
  const angaben = [wildart || null, zeit(eigene?.["Zeitpunkt"] ?? meldung?.geraetezeit)].filter(Boolean).join(" · ");
  const stand = meldung?.stand ?? "übertragen";
  const zurMeldungen = () => navigate(-1);
  const zurueck = () => { setFehler(null); if (schritt === "liste") zurMeldungen(); else setSchritt("liste"); };

  const ergaenzen = (daten: Record<string, unknown>) => {
    void einreihen("SchussErgaenzen", `Ergänzung zum Schuss ${schuss}`, umschlag({ Schuss: schuss, ...daten }));
    zurMeldungen();
  };
  const korrigieren = (ausgang: string) => {
    void einreihen("SchussausgangKorrigieren", `Korrektur zum Schuss ${schuss}`, umschlag({ Schuss: schuss, Schussausgang: ausgang }));
    zurMeldungen();
  };
  const gps = async () => {
    setFehler(null);
    const m = await positionMessen();
    if (!m) { setFehler("Keine Position verfügbar."); return; }
    ergaenzen({ Anschussposition: m.position, "Quelle Anschussposition": "vor Ort gemessen", "Genauigkeit Anschussposition": m.genauigkeit });
  };
  const ueberKarte = () => {
    const p = positionLesen(eingabe);
    if (!p) { setFehler("Keine Position verfügbar."); return; }
    setKarte(false);
    ergaenzen({ Anschussposition: p, "Quelle Anschussposition": "auf der Karte gesetzt" });
  };

  const zurueckKachel = <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={zurueck} />;
  const hinweis = (
    <div className="hinweis platte" data-zeile-fest="">
      <Symbol name="schuss" />
      <div><b>Schuss <span className="kennung">{schuss}</span></b><span>{angaben || "Schuss"}</span></div>
    </div>
  );

  if (schritt === "liste") {
    return rahmen({
      drueckjagdId: id, name: "Ergänzen", unter: <>Schuss <span className="kennung">{schuss}</span></>, zurueck, sperre: gesperrt,
      unten: <p className="liste__schutz platte" role="status"><span className="liste__text">Schuss <span className="kennung">{schuss}</span> · {stand}</span></p>,
      children: (
        <>
          <section className="liste-bereich" role="region" aria-label="Ergänzen">
            {LISTE.map(({ schritt: s, symbol }) => (
              <Button key={s} className="zeile zeile--knopf platte" aria-label={s} onPress={() => setSchritt(s)}>
                <Symbol name={symbol} />
                <span className="zeile__t">{s}</span>
                <Symbol name="weiter" className="zeile__pfeil" />
              </Button>
            ))}
          </section>
        </>
      ),
    });
  }

  return rahmen({
    drueckjagdId: id, name: schritt, titel: schritt, schritte: `Schuss ${meldungsIdAnzeige(schuss)}`, sperre: gesperrt,
    ablauf: schritt === "Fluchtrichtung" ? "ablauf--flucht" : schritt === "Anschuss" ? "ablauf--liegeort" : schritt === "Schussausgang" ? "ablauf--ausgang" : true,
    children: (
      <>
        {zurueckKachel}
        {schritt === "Fluchtrichtung" ? (
          <>
            <div className="r12">{hinweis}</div>
            <div className="r3">
              <div className="raster raster--3x3">
                {RASTER.map((r) => (r ? (
                  <Kachel key={r} symbol="fluchtrichtung" begriff={r} art="wahl" className={`k--richtung k--richtung-${r}`} onPress={() => ergaenzen({ Fluchtrichtung: r })} />
                ) : (
                  <div key="mitte" className="flucht__mitte platte" data-zeile-fest=""><Symbol name="schuss" /><span className="kennung">{schuss}</span></div>
                )))}
              </div>
            </div>
          </>
        ) : null}
        {schritt === "Schussausgang" ? (
          <>
            <div className="r12">
              <Kachel symbol="anschuss" begriff="Anschuss" art="wahl" className="k--breit" onPress={() => korrigieren("Anschuss")} />
              {hinweis}
            </div>
            <div className="r3">
              <div className="raster raster--2" data-gleich="">
                <Kachel symbol="stueck-liegt" begriff="Stück liegt" art="wahl" onPress={() => korrigieren("Stück liegt")} />
                <Kachel symbol="fehlschuss" begriff="Fehlschuss" art="wahl" onPress={() => korrigieren("Fehlschuss")} />
              </div>
            </div>
          </>
        ) : null}
        {schritt === "Pirschzeichen" ? (
          <>
            <div className="r12">{hinweis}</div>
            <div className="r3">
              <div className="raster raster--3" data-gleich="">
                {PIRSCHZEICHEN.map((p) => (
                  <Kachel key={p} symbol={SYMBOL_PIRSCHZEICHEN[p] ?? "pirschzeichen"} begriff={p} art="wahl" onPress={() => ergaenzen({ Pirschzeichen: [p] })} />
                ))}
              </div>
            </div>
          </>
        ) : null}
        {schritt === "Anschuss" ? (
          <>
            <div className="karte-gross platte" aria-hidden="true"><span className="marke-rund"><Symbol name="anschuss" /></span></div>
            <div>
              {fehler ? <Meldung>{fehler}</Meldung> : null}
              <div className="raster raster--2" data-gleich="">
                <Kachel symbol="karte" begriff="Karte" art="wahl" onPress={() => { setFehler(null); setKarte(true); }} />
                <Kachel symbol="standort" begriff="GPS-Messung" art="wahl" onPress={() => void gps()} />
              </div>
            </div>
            <Dialog name="Karte" symbol="karte" offen={karte} onSchliessen={() => setKarte(false)}>
              <Textfeld label="Position" wert={eingabe} onChange={setEingabe} mono eingabemodus="text" />
              <div className="knopfreihe">
                <Knopf art="zweit" onPress={() => setKarte(false)}>Abbrechen</Knopf>
                <Knopf onPress={ueberKarte}>Übernehmen</Knopf>
              </div>
            </Dialog>
          </>
        ) : null}
      </>
    ),
  });
}
